"use client";

import { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import Modal from "@/components/ui/Modal";
import Button from "@/components/ui/Button";
import { useAuth } from "@/contexts/AuthContext";
import { useSystemSettings } from "@/contexts/SystemSettingsContext";
import { useToast } from "@/contexts/ToastContext";
import { recordNearMeFunnelMetric } from "@/lib/nearMeMetricsClient";
import {
  buildLoginHref,
  sanitizeInternalPath,
} from "@/lib/navigation/authRedirect";
import {
  applyHomepageNearMeQuickOptIn,
  phoneDigitsFromProfilePhone,
  profileLocationUsableForNearMe,
} from "@/lib/notifications/homepage-near-me-quick-opt-in";
import { isPhoneValidForNearMeWhatsapp } from "@/lib/notifications/near-me-whatsapp-preference-sync";
import { supabase } from "@/lib/supabase";
import TelegramListingChannelsFollow from "@/components/telegram/TelegramListingChannelsFollow";
import TelegramNearMeConnectPanel from "@/components/telegram/TelegramNearMeConnectPanel";

const WHATSAPP_GROUP_LAGOS =
  "https://chat.whatsapp.com/CoJahuwza67JNUMmTKaEMn?mode=ems_copy_t";
const WHATSAPP_GROUP_OYO = "https://chat.whatsapp.com/Jpi7ShoDkxc4II5QCkC5Qf";
const WHATSAPP_SUPPORT = "https://wa.me/2348125554459";
import { SELL_IT_OFF_OPS_TELEGRAM_URL } from "@/lib/ops/sellItOffOpsContact";

const TELEGRAM_SUPPORT = SELL_IT_OFF_OPS_TELEGRAM_URL;

function TelegramSupportIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
    </svg>
  );
}

function WhatsAppSupportIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 6.045L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
    </svg>
  );
}

export const PROFILE_NEAR_ME_NOTIFICATIONS_HREF =
  "/profile?tab=notifications&nearMeSource=homepage";

export const PROFILE_LOCATION_HREF = "/profile?tab=profile";

function buildSignupHrefForNearMeAlerts(): string {
  const redirectTo = sanitizeInternalPath(PROFILE_NEAR_ME_NOTIFICATIONS_HREF);
  return `/signup?redirectTo=${encodeURIComponent(redirectTo)}`;
}

type HomepageNearMeAlertsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Called when user dismisses without opting in (snooze policy). */
  onNotNow?: () => void;
};

type PrefsLoadState = "idle" | "loading" | "ready" | "error";

export default function HomepageNearMeAlertsModal({
  isOpen,
  onClose,
  onNotNow,
}: HomepageNearMeAlertsModalProps) {
  const { user, isLoading: authLoading } = useAuth();
  const { showToast } = useToast();
  const {
    newListingNearMeNotificationsEnabled,
    nearMeUserDefaultsPolicy,
    isLoading: settingsLoading,
  } = useSystemSettings();

  const nearMeAvailable =
    !settingsLoading && newListingNearMeNotificationsEnabled;

  const [prefsLoadState, setPrefsLoadState] = useState<PrefsLoadState>("idle");
  const [nearMeAlreadyOn, setNearMeAlreadyOn] = useState(false);
  const [notifyWhatsapp, setNotifyWhatsapp] = useState(false);
  const [waOptInAt, setWaOptInAt] = useState<string | null>(null);
  const [waOptOutAt, setWaOptOutAt] = useState<string | null>(null);
  const [waCurrentlyOn, setWaCurrentlyOn] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSucceeded, setSaveSucceeded] = useState(false);

  const phoneDigits = phoneDigitsFromProfilePhone(user?.phone ?? null);
  const phoneOk = isPhoneValidForNearMeWhatsapp(phoneDigits);
  const locationOk = user
    ? profileLocationUsableForNearMe({
        location: user.location,
        location_state_id: user.location_state_id,
        location_lga_id: user.location_lga_id,
        location_neighborhood_id: user.location_neighborhood_id,
      })
    : false;

  const nearMeOn = nearMeAlreadyOn || saveSucceeded;

  const resetSignedInState = useCallback(() => {
    setPrefsLoadState("idle");
    setNearMeAlreadyOn(false);
    setNotifyWhatsapp(false);
    setWaOptInAt(null);
    setWaOptOutAt(null);
    setWaCurrentlyOn(false);
    setIsSaving(false);
    setSaveSucceeded(false);
  }, []);

  const handleClose = useCallback(() => {
    resetSignedInState();
    onClose();
  }, [onClose, resetSignedInState]);

  const handleNotNow = useCallback(() => {
    onNotNow?.();
    handleClose();
  }, [handleClose, onNotNow]);

  useEffect(() => {
    if (!isOpen || authLoading || !user?.id) return;
    void recordNearMeFunnelMetric("near_me_entrypoint_impression", {
      source: "homepage",
    });
  }, [authLoading, isOpen, user?.id]);

  useEffect(() => {
    if (!isOpen || authLoading || !user?.id || !nearMeAvailable) return;

    let cancelled = false;

    void (async () => {
      const { data, error } = await supabase
        .from("notification_preferences")
        .select(
          "notify_new_listings_near_me, near_me_notify_whatsapp, near_me_whatsapp_opt_in_at, near_me_whatsapp_opt_out_at",
        )
        .eq("user_id", user.id)
        .maybeSingle();

      if (cancelled) return;

      if (error) {
        console.error("[homepage_near_me] prefs load:", error);
        setPrefsLoadState("error");
        return;
      }

      const optedIn = Boolean(data?.notify_new_listings_near_me);
      const waOn = Boolean(data?.near_me_notify_whatsapp);
      setNearMeAlreadyOn(optedIn);
      setWaCurrentlyOn(waOn);
      setNotifyWhatsapp(waOn);
      setWaOptInAt(data?.near_me_whatsapp_opt_in_at ?? null);
      setWaOptOutAt(data?.near_me_whatsapp_opt_out_at ?? null);
      setPrefsLoadState("ready");
    })();

    return () => {
      cancelled = true;
    };
  }, [authLoading, isOpen, nearMeAvailable, user?.id]);

  const handleQuickOptIn = async () => {
    if (!user?.id || !locationOk || isSaving) return;

    if (notifyWhatsapp && !phoneOk) {
      showToast(
        "Add a valid phone number on your Profile before enabling WhatsApp alerts.",
        "error",
      );
      return;
    }

    setIsSaving(true);
    try {
      const nowIso = new Date().toISOString();
      const result = await applyHomepageNearMeQuickOptIn(supabase, {
        userId: user.id,
        nowIso,
        notifyWhatsapp,
        phoneDigits,
        previousWhatsapp: {
          enabled: waCurrentlyOn,
          opt_in_at: waOptInAt,
          opt_out_at: waOptOutAt,
        },
        nearMePolicy: nearMeUserDefaultsPolicy,
      });

      if (!result.ok) {
        if (result.code === "whatsapp_phone_invalid") {
          showToast(
            "Add a valid phone number on your Profile before enabling WhatsApp alerts.",
            "error",
          );
        } else {
          showToast(
            "Could not save near-me alerts. Please try again.",
            "error",
          );
        }
        return;
      }

      setNearMeAlreadyOn(true);
      setWaCurrentlyOn(result.row.near_me_notify_whatsapp);
      setWaOptInAt(result.row.near_me_whatsapp_opt_in_at);
      setWaOptOutAt(result.row.near_me_whatsapp_opt_out_at);
      setSaveSucceeded(true);

      void recordNearMeFunnelMetric("near_me_prompt_accepted", {
        source: "homepage",
      });
      void recordNearMeFunnelMetric("near_me_settings_saved", {
        source: "homepage",
        near_me_enabled: true,
        near_me_email_enabled: true,
        near_me_whatsapp_enabled: result.row.near_me_notify_whatsapp,
        granularity: result.row.new_listings_near_me_granularity,
        delivery_mode: result.row.near_me_delivery_mode,
        categories_count: 0,
        subcategories_count: 0,
      });

      showToast("Near-me alerts are on.", "success");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSignupClick = () => {
    handleClose();
  };

  const renderStep1Body = () => {
    if (authLoading) {
      return <p className="text-xs text-text-light">Checking your account…</p>;
    }

    if (!user) {
      return (
        <div className="space-y-2">
          <p className="text-xs text-text-light">
            Create a free account so we can match listings to your location.
          </p>
          <Link
            href={buildSignupHrefForNearMeAlerts()}
            className="block"
            onClick={handleSignupClick}
          >
            <Button
              type="button"
              variant="primary"
              className="w-full text-white"
            >
              Create a free account
            </Button>
          </Link>
          <p className="text-center text-xs text-text-light">
            Already have an account?{" "}
            <Link
              href={buildLoginHref(PROFILE_NEAR_ME_NOTIFICATIONS_HREF)}
              className="font-medium text-primary hover:underline"
              onClick={handleClose}
            >
              Log in
            </Link>
          </p>
        </div>
      );
    }

    if (prefsLoadState === "loading" || prefsLoadState === "idle") {
      return (
        <p className="text-xs text-text-light">Loading your preferences…</p>
      );
    }

    if (prefsLoadState === "error") {
      return (
        <div className="space-y-2">
          <p className="text-xs text-red-600">
            Could not load your notification settings.
          </p>
          <Link
            href={PROFILE_NEAR_ME_NOTIFICATIONS_HREF}
            className="text-xs font-medium text-primary hover:underline"
            onClick={handleClose}
          >
            Open Profile → Notifications
          </Link>
        </div>
      );
    }

    if (!locationOk) {
      return (
        <div className="space-y-2">
          <p className="text-xs text-text-light">
            Add a location on your Profile so we know which listings are near
            you.
          </p>
          <Link
            href={PROFILE_LOCATION_HREF}
            className="block"
            onClick={handleClose}
          >
            <Button
              type="button"
              variant="primary"
              className="w-full text-white"
            >
              Add profile location
            </Button>
          </Link>
        </div>
      );
    }

    if (nearMeOn && saveSucceeded) {
      return (
        <div className="space-y-2">
          <p className="rounded-md bg-primary-50 px-3 py-2 text-xs text-primary-800">
            Near-me alerts are on for your profile location.
          </p>
          <Link
            href={PROFILE_NEAR_ME_NOTIFICATIONS_HREF}
            className="block text-center text-xs font-medium text-primary hover:underline"
            onClick={handleClose}
          >
            Customize distance, categories, and delivery
          </Link>
        </div>
      );
    }

    if (nearMeAlreadyOn && !saveSucceeded) {
      return (
        <div className="space-y-2">
          <p className="rounded-md bg-primary-50 px-3 py-2 text-xs text-primary-800">
            Near-me alerts are already on.
          </p>
          <Link
            href={PROFILE_NEAR_ME_NOTIFICATIONS_HREF}
            className="block"
            onClick={handleClose}
          >
            <Button
              type="button"
              variant="primary"
              className="w-full text-white"
            >
              Customize in Profile
            </Button>
          </Link>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <p className="text-xs text-text-light">
          Turn on a daily summary when matching listings are approved near your
          location (email / in-app by default).
        </p>
        <Button
          type="button"
          variant="primary"
          className="w-full text-white"
          disabled={isSaving}
          onClick={() => void handleQuickOptIn()}
        >
          {isSaving ? "Saving…" : "Turn on near-me alerts"}
        </Button>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <Link
            href={PROFILE_NEAR_ME_NOTIFICATIONS_HREF}
            className="text-center text-xs font-medium text-primary hover:underline sm:text-left"
            onClick={handleClose}
          >
            Customize before turning on
          </Link>
          {onNotNow ? (
            <button
              type="button"
              className="text-center text-xs font-medium text-text-light hover:text-foreground"
              onClick={handleNotNow}
            >
              Not now
            </button>
          ) : null}
        </div>
        <details className="rounded-md border border-border bg-surface px-3 py-2">
          <summary className="cursor-pointer text-xs font-medium text-foreground">
            Optional: also WhatsApp
          </summary>
          <label className="mt-2 flex items-start gap-2 text-xs text-foreground">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-primary disabled:opacity-60"
              checked={notifyWhatsapp}
              disabled={!phoneOk || isSaving}
              onChange={(e) => setNotifyWhatsapp(e.target.checked)}
            />
            <span>
              <span className="font-medium">Notify me on WhatsApp too</span>
              <span className="mt-0.5 block text-text-light">
                {phoneOk
                  ? "Sends each match as it is approved. Uses the phone on your Profile."
                  : "Add a valid phone on your Profile to enable."}
              </span>
            </span>
          </label>
        </details>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Get alerts for listings near you"
      maxWidth="md"
    >
      <div className="space-y-6">
        <p className="text-sm text-text-light">
          Know when approved items are available near you — then choose how you
          want to hear about them on Telegram.
        </p>

        {/* Step 1 — Turn on near-me */}
        <section className="space-y-3 rounded-lg border border-primary/30 bg-primary/5 p-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-primary-700">
              Step 1
            </p>
            <h3 className="text-sm font-semibold text-foreground">
              Turn on near-me alerts
            </h3>
          </div>
          {nearMeAvailable ? (
            renderStep1Body()
          ) : (
            <p className="text-xs text-text-light">
              Personalized near-me alerts are temporarily unavailable. You can
              still follow Telegram listing feeds below.
            </p>
          )}
        </section>

        {/* Step 2 — Telegram channels (personal + public) */}
        <section className="space-y-4 rounded-lg border border-border bg-surface p-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-text-light">
              Step 2
            </p>
            <h3 className="text-sm font-semibold text-foreground">
              Alert channels on Telegram
            </h3>
            <p className="mt-0.5 text-xs text-text-light">
              Connect once for private tips about items near you. Extra
              state-wide feeds and groups are optional below.
            </p>
          </div>

          <div className="space-y-2 rounded-md border border-border bg-secondary/40 p-3">
            <p className="text-xs font-semibold text-foreground">
              Personal alerts (private bot chat)
            </p>
            {user ? (
              <TelegramNearMeConnectPanel
                nearMeOptIn={nearMeOn}
                compact
                showHeading={false}
                surface="homepage"
              />
            ) : (
              <p className="text-xs text-text-light">
                Sign in (Step 1), then connect the Sell It Off bot for private
                nearby tips — not the same as the public channels below.
              </p>
            )}
          </div>

          <details className="rounded-md border border-dashed border-border px-3 py-2">
            <summary className="cursor-pointer text-xs font-medium text-text-light hover:text-foreground">
              Also browse all Lagos or Oyo listings
            </summary>
            <div className="mt-2">
              <TelegramListingChannelsFollow
                heading="Public Telegram channels"
                description="Scroll every new listing posted in that state. Anyone can join — these are open feeds, not private tips about items near you."
              />
            </div>
          </details>

          <details className="rounded-md border border-dashed border-border px-3 py-2">
            <summary className="cursor-pointer text-xs font-medium text-text-light hover:text-foreground">
              Also join WhatsApp marketplace groups
            </summary>
            <p className="mt-2 text-xs text-text-light">
              Community chat groups where people buy and sell. Separate from
              private nearby alerts and from the Telegram channels above.
            </p>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant="outline"
                className="w-full border-[#25D366] text-[#128C7E] hover:bg-[#eafff1]"
                onClick={() =>
                  window.open(
                    WHATSAPP_GROUP_LAGOS,
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
              >
                Lagos
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full border-[#25D366] text-[#128C7E] hover:bg-[#eafff1]"
                onClick={() =>
                  window.open(
                    WHATSAPP_GROUP_OYO,
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
              >
                Oyo
              </Button>
            </div>
          </details>
        </section>

        <section className="border-t border-border pt-4">
          <h3 className="text-sm font-semibold text-foreground">Need help?</h3>
          <p className="mt-0.5 text-xs text-text-light">
            Questions about buying, selling, or your account.
          </p>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant="outline"
              className="w-full border-border text-foreground hover:bg-secondary"
              onClick={() =>
                window.open(TELEGRAM_SUPPORT, "_blank", "noopener,noreferrer")
              }
            >
              <span className="inline-flex items-center justify-center gap-1.5">
                <TelegramSupportIcon className="h-4 w-4 shrink-0" />
                Telegram
              </span>
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full border-border text-foreground hover:bg-secondary"
              onClick={() =>
                window.open(WHATSAPP_SUPPORT, "_blank", "noopener,noreferrer")
              }
            >
              <span className="inline-flex items-center justify-center gap-1.5">
                <WhatsAppSupportIcon className="h-4 w-4 shrink-0" />
                WhatsApp
              </span>
            </Button>
          </div>
        </section>
      </div>
    </Modal>
  );
}
